import { useSelector } from "react-redux";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Link } from "react-router-dom";
import SubmissionReview from "../components/admin/SubmissionReview";
import api from "@/utils/api";
import { useEffect, useState } from "react";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import DistrictAdminDashboard from "../components/dashboard/DistrictAdminDashboard";
import AdminDashboard from "../components/dashboard/AdminDashboard";
import { LoadingSpinner } from "@/components/ui/loading-spinner";
import StatusBadge from "../components/common/StatusBadge";

const toDateStr = (date) => {
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, "0");
  const day = String(date.getDate()).padStart(2, "0");

  return `${year}-${month}-${day}`;
};

const formatDisplayDate = (dateStr) => {
  if (!dateStr) return "";

  const date = new Date(dateStr);

  const day = String(date.getDate()).padStart(2, "0");
  const month = String(date.getMonth() + 1).padStart(2, "0");
  const year = date.getFullYear();

  return `${day}-${month}-${year}`;
};

const getDateRange = (filter) => {
  const now = new Date();
  const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());

  switch (filter) {
    case "overall":
      return { from: "", to: "" };

    case "today":
      return { from: toDateStr(today), to: toDateStr(today) };

    case "yesterday": {
      const yesterday = new Date(today);
      yesterday.setDate(today.getDate() - 1);
      return { from: toDateStr(yesterday), to: toDateStr(yesterday) };
    }

    case "this_month": {
      const start = new Date(today.getFullYear(), today.getMonth(), 1);
      const end = new Date(today.getFullYear(), today.getMonth() + 1, 0);
      return { from: toDateStr(start), to: toDateStr(end) };
    }

    case "previous_month": {
      const start = new Date(today.getFullYear(), today.getMonth() - 1, 1);
      const end = new Date(today.getFullYear(), today.getMonth(), 0);
      return { from: toDateStr(start), to: toDateStr(end) };
    }

    case "previous_year": {
      const start = new Date(today.getFullYear() - 1, 0, 1);
      const end = new Date(today.getFullYear() - 1, 11, 31);
      return { from: toDateStr(start), to: toDateStr(end) };
    }

    case "current_financial_year": {
      const fyStartYear =
        today.getMonth() >= 3 ? today.getFullYear() : today.getFullYear() - 1;
      const start = new Date(fyStartYear, 3, 1);
      const end = new Date(fyStartYear + 1, 2, 31);
      return { from: toDateStr(start), to: toDateStr(end) };
    }

    case "previous_financial_year": {
      const fyStartYear =
        today.getMonth() >= 3
          ? today.getFullYear() - 1
          : today.getFullYear() - 2;
      const start = new Date(fyStartYear, 3, 1);
      const end = new Date(fyStartYear + 1, 2, 31);
      return { from: toDateStr(start), to: toDateStr(end) };
    }

    default:
      return { from: "", to: "" };
  }
};

const buildFilterOptions = () => {
  const now = new Date();
  const curFyStart =
    now.getMonth() >= 3 ? now.getFullYear() : now.getFullYear() - 1;
  const prevFyStart = curFyStart - 1;

  const sy = (y) => String(y).slice(2);

  return [
    { value: "overall", label: "All" },
    { value: "today", label: "Today" },
    { value: "yesterday", label: "Yesterday" },
    { value: "this_month", label: "This Month" },
    { value: "previous_month", label: "Previous Month" },
    { value: "previous_year", label: "Previous Year" },
    {
      value: "current_financial_year",
      label: `Current Financial Year (Apr ${sy(curFyStart)} – Mar ${sy(curFyStart + 1)})`,
    },
    {
      value: "previous_financial_year",
      label: `Previous Financial Year (Apr ${sy(prevFyStart)} – Mar ${sy(prevFyStart + 1)})`,
    },
  ];
};

const FILTER_OPTIONS = buildFilterOptions();

const Dashboard = () => {
  const { user } = useSelector((state) => state.auth);
  const { forms, submissions } = useSelector((state) => state.forms);
  const [loading, setLoading] = useState(false);
  const [dashboardData, setDashboardData] = useState([]);
  const [formsStatus, setFormsStatus] = useState({
    modernization: [],
    new_shuttleless: [],
    cfc_subsidies: [],
    air_jet_loom: [],
  });

  const [selectedFilter, setSelectedFilter] = useState("overall");
  const [dateRange, setDateRange] = useState({ from: "", to: "" });

  useEffect(() => {
    checkFormsStatus({ from: "", to: "" });
  }, []);

  const handleFilterChange = (value) => {
    setSelectedFilter(value);
    const range = getDateRange(value);
    setDateRange(range);
    checkFormsStatus(range);
  };

  const checkFormsStatus = async (range = dateRange) => {
    try {
      setLoading(true);

      const params = new URLSearchParams();
      if (range.from) params.append("from_date", range.from);
      if (range.to) params.append("to_date", range.to);
      const query = params.toString() ? `?${params.toString()}` : "";

      if (user?.role === "admin") {
        const res = await api.get(`/admin_dashboard${query}`);
        setDashboardData(res?.data);
      } else if (user?.role === "district_admin") {
        const res = await api.get(`/district_admin_dashboard${query}`);
        setDashboardData(res?.data);
      } else {
        const res = await api.get(`/profile/userApplications${query}`);
        setFormsStatus(
          res.data.data || {
            modernization: [],
            new_shuttleless: [],
            cfc_subsidies: [],
          },
        );
      }
    } catch (error) {
      console.error("Error checking forms status:", error);
    } finally {
      setLoading(false);
    }
  };

  const filterLabel =
    selectedFilter !== "overall" && dateRange.from && dateRange.to
      ? `${formatDisplayDate(dateRange.from)} to ${formatDisplayDate(dateRange.to)}`
      : null;

  const FilterSelect = () => (
    <div className="flex items-center gap-3">
      <div>
        <label className="text-xs font-semibold text-muted-foreground uppercase tracking-wide">
          Filter By Date Range
        </label>
        {filterLabel && (
          <p className="text-xs p-2 text-muted-foreground hidden sm:block">
            {filterLabel}
          </p>
        )}
      </div>
      <div className="flex flex-col items-end gap-1">
        <Select value={selectedFilter} onValueChange={handleFilterChange}>
          <SelectTrigger className="w-[260px]">
            <SelectValue placeholder="Select date range" />
          </SelectTrigger>
          <SelectContent>
            {FILTER_OPTIONS.map((opt) => (
              <SelectItem key={opt.value} value={opt.value}>
                {opt.label}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>
    </div>
  );

  const ApplicationStatusTable = () => {
    const allApplications = [
      ...formsStatus.modernization.map((app) => ({
        ...app,
        type: "modernization",
        title: "Upgradation",
      })),
      ...formsStatus.new_shuttleless.map((app) => ({
        ...app,
        type: "new_shuttleless",
        title: "New Shuttleless",
      })),
      ...formsStatus.cfc_subsidies.map((app) => ({
        ...app,
        type: "cfc",
        title: "CFC Scheme",
      })),
      ...(formsStatus?.air_jet_loom || []).map((app) => ({
        ...app,
        type: "new_airjetlooms",
        title: "Upgradation",
      })),
    ];

    const handleDownload = (fileUrl, fileName) => {
      const link = document.createElement("a");
      link.href = fileUrl;
      link.download = fileName;
      link.click();
    };

    if (allApplications.length === 0) {
      return (
        <Card>
          <CardHeader>
            <CardTitle>My Applications</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-center py-8 text-muted-foreground">
              No applications submitted yet.
            </div>
          </CardContent>
        </Card>
      );
    }

    return (
      <Card>
        <CardHeader>
          <CardTitle className="flex justify-between items-center mb-5">
            <span>My Applications</span>
          </CardTitle>
        </CardHeader>
        <CardContent className="bg-white rounded-lg m-2 p-2">
          <Table>
            <TableHeader className="bg-teal-700 rounded">
              <TableRow>
                <TableHead className="text-white font-semibold text-md">
                  Application Number
                </TableHead>
                <TableHead className="text-white font-semibold text-md">
                  Scheme Type
                </TableHead>
                <TableHead className="text-white font-semibold text-md">
                  Applicant Name
                </TableHead>
                <TableHead className="text-white font-semibold text-md">
                  Submitted On
                </TableHead>
                <TableHead className="text-white font-semibold text-md">
                  Status
                </TableHead>
                <TableHead className="text-white font-semibold text-md">
                  Application
                </TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {allApplications.map((application, index) => (
                <TableRow
                  key={`${application.type}-${application.id || index}`}
                >
                  <TableCell className="font-medium">
                    {application.application_no ||
                      application.application_number ||
                      ""}
                  </TableCell>
                  <TableCell>{application.title}</TableCell>
                  <TableCell>
                    {application?.applicant_name ||
                      application?.user?.name ||
                      ""}
                  </TableCell>
                  <TableCell>
                    {application.submitted_on ? (
                      <>
                        {new Date(application.submitted_on)
                          .toLocaleDateString("en-GB")
                          .replace(/\//g, " - ")}
                        <br />
                        {new Date(application.submitted_on).toLocaleTimeString(
                          "en-GB",
                          {
                            hour: "2-digit",
                            minute: "2-digit",
                            hour12: true,
                          },
                        )}
                      </>
                    ) : (
                      "-"
                    )}
                  </TableCell>
                  <TableCell>
                    <StatusBadge status={application.status} />
                  </TableCell>
                  <TableCell>
                    {application.status !== "incomplete" &&
                    application.status !== "started" ? (
                      <div className="flex gap-3">
                        {application.status ===
                          "provisional_sanction_recommended" && (
                          <Button
                            variant="outline"
                            asChild
                            className="p-2 text-xs w-full"
                          >
                            <Link
                              to={`/scheme/${application?.type}/${application?.id}`}
                            >
                              Accept Provisional
                              <br /> Sanction Letter
                            </Link>
                          </Button>
                        )}
                        <Button
                          type="button"
                          onClick={() =>
                            handleDownload(
                              application?.application_url,
                              `${application?.application_no}.pdf`,
                            )
                          }
                          className="p-2 text-xs w-full"
                        >
                          Download <br /> Application
                        </Button>
                        <Button
                          type="button"
                          onClick={() =>
                            handleDownload(
                              application?.application_uploads,
                              `${application?.application_no}.zip`,
                            )
                          }
                          className="p-2 text-xs w-full"
                        >
                          Download <br /> Attachments
                        </Button>
                      </div>
                    ) : (
                      <Button
                        variant="outline"
                        asChild
                        className="p-2 text-xs w-full"
                      >
                        <Link
                          to={`/scheme/${application?.type}/${application?.id}`}
                        >
                          Continue Application
                        </Link>
                      </Button>
                    )}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </CardContent>
      </Card>
    );
  };

  const getStats = () => {
    if (user?.role === "applicant") {
      return [
        {
          title: "Submitted Forms",
          value: submissions.filter((s) => s.user_id === user.id).length,
          description: "Your form submissions",
        },
        {
          title: "Application Status",
          value: "Not Started",
          description: "Forms you can submit",
        },
      ];
    } else if (user?.role === "district_admin") {
      return [
        {
          title: "Total Submissions",
          value: submissions.length,
          description: "All form submissions",
        },
        {
          title: "Pending Reviews",
          value: submissions.filter((s) => s.status === "submitted").length,
          description: "Submissions awaiting review",
        },
        {
          title: "Assigned Visitors",
          value: 5,
          description: "Visitors in your district",
        },
        { title: "Modernization Form", value: forms.length, description: "" },
        {
          title: "New Shuttle Less Form",
          value: forms.length,
          description: "",
        },
        { title: "CFC Subsidy Form", value: forms.length, description: "" },
      ];
    } else if (user?.role === "district_visitor") {
      return [
        {
          title: "Assigned Submissions",
          value: submissions.filter((s) => s.visitor_id === user.id).length,
          description: "Submissions assigned to you",
        },
        {
          title: "Pending Verification",
          value: submissions.filter(
            (s) => s.visitor_id === user.id && s.status === "assigned",
          ).length,
          description: "Submissions needing verification",
        },
      ];
    } else if (user?.role === "admin") {
      return [
        { title: "Total Users", value: 125, description: "All system users" },
        {
          title: "Total Submissions",
          value: submissions.length,
          description: "All form submissions",
        },
        {
          title: "Active Forms",
          value: forms.length,
          description: "Available forms",
        },
        { title: "Modernization Form", value: forms.length, description: "" },
        {
          title: "New Shuttle Less Form",
          value: forms.length,
          description: "",
        },
        { title: "CFC Subsidy Form", value: forms.length, description: "" },
      ];
    }
    return [];
  };

  const stats = getStats();

  return (
    <>
      {loading ? (
        <div className="flex justify-center py-8">
          <LoadingSpinner />
        </div>
      ) : (
        <>
          {user?.role === "applicant" && (
            <div className="space-y-6 max-w-[95vw] mx-auto">
              <div className="flex items-center justify-between">
                <h1 className="text-3xl font-bold">Dashboard</h1>
                <div className="flex items-center gap-3"> 
                  <Button asChild>
                    <Link to="/scheme">View Scheme's</Link>
                  </Button>
                </div>
              </div>
              <ApplicationStatusTable />
              {user?.role !== "applicant" && <SubmissionReview />}
            </div>
          )}

          {user?.role === "district_admin" && (
            <div className="space-y-6 p-6 rounded-2xl bg-white shadow-md hover:shadow-lg transition-all">
              <div className="flex items-center justify-between">
                <h1 className="text-3xl font-bold">Dashboard</h1>
                <FilterSelect />
              </div>
              <DistrictAdminDashboard
                dashboardData={dashboardData}
                dateRange={dateRange} 
              />
            </div>
          )}

          {user?.role === "admin" && (
            <div className="space-y-6 p-6 rounded-2xl bg-white shadow-md hover:shadow-lg transition-all">
              <div className="flex items-center justify-between">
                <h1 className="text-3xl font-bold">Dashboard</h1>
                <FilterSelect />
              </div>
              <AdminDashboard
                dashboardData={dashboardData}
                dateRange={dateRange} 
              />
            </div>
          )}
        </>
      )}
    </>
  );
};

export default Dashboard;
